/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { css } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import Portal from '@atlaskit/portal';
import { token } from '@atlaskit/tokens';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store';

import { CONVERSATION_ASSISTANT_MINIMUM_HEIGHT } from '../../common/constants/public';
import { useUIConfig } from '../../common/utils/ui-config';
import { ConversationAssistantUIModes } from '../../types';

const styles = cssMap({
	wrapper: {
		boxSizing: 'border-box',
		width: `calc(100% - ${token('space.150')})`,
		marginTop: token('space.200'),
		height: `calc(100% - ${token('space.200')})`,
		backgroundColor: token('elevation.surface'),
		display: 'flex',
		flexDirection: 'column',
		alignContent: 'stretch',
		alignItems: 'stretch',
	},

	wrapperNav4: {
		boxSizing: 'border-box',
		width: '100%',
		height: '100%',
		backgroundColor: token('elevation.surface'),
		display: 'flex',
		flexDirection: 'column',
		alignContent: 'stretch',
		alignItems: 'stretch',
	},

	wrapperHeight: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		minHeight: `${CONVERSATION_ASSISTANT_MINIMUM_HEIGHT}px`,
	},

	externalEmbed: {
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		width: '100%',
		height: '100%',
	},

	fullscreenDebug: {
		position: 'fixed',
		inset: token('space.0'),
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		height: 'unset',
		width: 'unset',
	},
	/* If the staging area is open, we need to reduce the width of chat to allow the staging area to render next to it */
	halfWidth: {
		width: '50%',
	},
});

const stylesBorder = cssMap({
	borderNavV3: {
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderTopLeftRadius: token('radius.large'),
		borderTopRightRadius: token('radius.large'),
	},

	/**
	 * The Nav 4 `Panel` page layout slot will have a `1px` left border.
	 *
	 * No border is required by the consumer.
	 */
	borderNavV4: {},

	borderFullScreenDebug: {
		borderStyle: 'solid',
		borderColor: token('color.border.accent.blue'),
		borderWidth: token('radius.large'),
		borderRadius: token('radius.large'),
	},

	// empty on purpose so every type of border variation has styles defining, this is to avoid linter error
	borderExternalEmbed: {},

	borderStagingArea: {
		borderRadius: token('radius.xlarge'),
		boxShadow: token('elevation.shadow.raised'),
	},
});

const miniModalWrapperStyles = css({
	borderRadius: token('radius.xxlarge'),
	boxShadow: token('elevation.shadow.overlay'),
	overflow: 'hidden',
	// default values used by @atlaskit/motion's easeInOut and medium duration
	transition: 'max-height 350ms cubic-bezier(0.15,1,0.3,1)',
});

export type MainWrapperProps = {
	testId?: string;
	isNav4Enabled?: boolean;
	children: ReactNode;
	mode?: ConversationAssistantUIModes;
};

export const MainWrapper = ({ testId, children, isNav4Enabled, mode }: MainWrapperProps) => {
	const isMiniModal = mode === ConversationAssistantUIModes.MINI_MODAL;

	const { externalEmbed, fullScreen, elements } = useUIConfig();
	const { stagingAreaMode } = useStagingAreaState();

	const shouldShowStagingArea = stagingAreaMode === 'inline';

	// Purely for FE team testing fullscreen where branch deploys are accessible
	// The use of localStorage and a private FF should make this safe, and it's our
	// best bet to improve dev velocity at the moment. Once Atlas has local consumption
	// we can remove this
	const fullscreenDebugEnabled =
		localStorage.getItem('DEBUG_rovo_chat_fullscreen') === 'true' &&
		fg('rovo_chat_show_local_frontend_components');

	const shouldUseWrapperNav4Styles = isNav4Enabled;

	if (fullscreenDebugEnabled) {
		return (
			<Portal zIndex={500}>
				<div
					data-testid={testId}
					css={[
						shouldUseWrapperNav4Styles ? styles.wrapperNav4 : styles.wrapper,
						isMiniModal ? miniModalWrapperStyles : styles.wrapperHeight,
						externalEmbed && styles.externalEmbed,
						styles.fullscreenDebug,
						stylesBorder.borderFullScreenDebug,
					]}
				>
					{children}
				</div>
			</Portal>
		);
	}

	const getBorderStyle = (): keyof typeof stylesBorder => {
		if (elements?.stagingAreaEnabled) {
			return 'borderStagingArea';
		}

		if (externalEmbed) {
			return 'borderExternalEmbed';
		}

		if (fullscreenDebugEnabled) {
			// this is not possible because if condition above, but adding it here for completeness
			return 'borderFullScreenDebug';
		}

		if (shouldUseWrapperNav4Styles) {
			return 'borderNavV4';
		}

		return 'borderNavV3';
	};

	return (
		<div
			data-testid={testId}
			css={[
				shouldUseWrapperNav4Styles ? styles.wrapperNav4 : styles.wrapper,
				isMiniModal ? miniModalWrapperStyles : styles.wrapperHeight,
				externalEmbed && styles.externalEmbed,
				stylesBorder[getBorderStyle()],
				fullScreen && shouldShowStagingArea && styles.halfWidth,
			]}
		>
			{children}
		</div>
	);
};
